<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useTranslation } from "i18next-vue";

import type { Headers, PaginationMeta } from "@/types/rest";
import type {
  ConfiguredEntity,
  IntegrationInstance,
  EntityFilterData
} from "@/types/integrationInstance";

import type { Page } from "@/types/page";
import type { NewGroupData, Group } from "@/types/group";
import type { ChangeCallbackParams } from "@/types/config";

import { useComponentErrorCapture } from "@/mixins/error";
import { useTiming } from "@/mixins/timing";
import { useWindowDimension } from "@/mixins/windowDimension";
import { focusInput } from "@/mixins/device";
import { getPaginationLimit, savePaginationLimit } from "@/mixins/listing";

import { integrationsStore } from "@/stores/integrations";
import { profileStore } from "@/stores/profile";
import { addErrorBottom } from "@/stores/messages";

import Modal from "@/components/elements/Modal.vue";
import Dialog from "@/components/elements/Dialog.vue";
import IconSelect from "@/components/elements/icon/IconSelect.vue";
import UCInput from "@/components/ui/UCInput.vue";
import UCToggle from "@/components/ui/UCToggle.vue";
import EntityListFiltered from "@/components/elements/entity/EntityListFiltered.vue";

const { t } = useTranslation();
const { sleep } = useTiming();
const { isMobileScreen } = useWindowDimension();

const integrationStorage = integrationsStore();

useComponentErrorCapture();

const props = defineProps({
  page: {
    type: Object,
    required: false,
  }
});

defineExpose({
  open
});
const emit = defineEmits(["close"]);

const groupSkeleton = {
  name: "",
  icon: "uc:layer-group",
  description: "",
};

const entityListFilter = ref(<EntityFilterData>{
  searchText: '',
  entityTypes: '',
  instances: ''
});

const profileStorage = profileStore();

const newGroup = ref<NewGroupData>(JSON.parse(JSON.stringify(groupSkeleton as unknown as NewGroupData)));
const filteredEntities = ref<ConfiguredEntity[]>([]);
const instances = ref<IntegrationInstance[]>([]);

const assignedEntities = ref<ConfiguredEntity[]>([]);

const activeStep = ref(1);
const slideRight = ref(false);
const showModal = ref(false);
const addToPage = ref(true);
const errorMessage = ref("");

const dialogCreateGroup = ref();

const pagination = ref<PaginationMeta>({
  limit: getPaginationLimit() ?? 20,
  page: 1,
});

watch(showModal, async (val) => {
  newGroup.value = JSON.parse(JSON.stringify(groupSkeleton as unknown as NewGroupData));
  assignedEntities.value = [];
  if (val == true) {
    pagination.value = {
      limit: getPaginationLimit() ?? 20,
      page: 1,
    };
    fetchFilteredEntities(true);
    try {
      instances.value = await integrationStorage.getInstances();
    } catch (e: any) {
      addErrorBottom(e);
    }
  }
});

watch(activeStep, () => {
  const modalAddGroup = document.querySelector('.modal--add-group') as HTMLElement;
  if (modalAddGroup) {
    focusInput(modalAddGroup, true);
  }
});

watch(
  () => pagination.value.limit,
  (val) => {
    savePaginationLimit(val);
  }
);

const stepTransition = computed(() => {
  return slideRight.value == true ? 'slide-tab-right' : 'slide-tab-left';
});

integrationStorage.$subscribe((mutation, state) => {
  if (state.instances) {
    instances.value = state.instances;
  }
});

function changeFilter(data: EntityFilterData) {
  entityListFilter.value = data;
  fetchFilteredEntities(true);
}

function reloadEntities() {
  fetchFilteredEntities(true);
}

async function fetchFilteredEntities(userFetchFirstPage: boolean = false) {
  if (userFetchFirstPage === true) {
    pagination.value.page = 1;
  }

  let searchText = entityListFilter.value.searchText;

  try {
    const entList = await integrationStorage.getConfiguredEntitiesByPageByLimit(entityListFilter.value.instances || '', false, pagination.value.page, pagination.value.limit, searchText, entityListFilter.value.entityTypes);
    filteredEntities.value = entList.data.configuredEntities as ConfiguredEntity[];

    const listHeaders = entList.headers as Headers;
    if (listHeaders) {
      const headerLimit = Number(listHeaders['pagination-limit']);
      const newLimit = headerLimit <= Number(listHeaders['pagination-count']) ? pagination.value.limit : headerLimit;
      pagination.value = {
        count: Number(listHeaders['pagination-count']) || 0,
        limit: newLimit || 0,
        page: Number(listHeaders['pagination-page']) || 0,
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function open() {
  showModal.value = true;
  activeStep.value = 1;
}

function closeModal() {
  showModal.value = false;
  activeStep.value = 1;
}

function goToStep(step: number) {
  clearErrors();
  if (step == 2 && newGroup.value.name.length < 1) {
    return errorMessage.value = t("ui.required_field");
  }
  
  slideRight.value = step < activeStep.value ? true : false;
  activeStep.value = step;
}

async function changeGroupIcon(params: ChangeCallbackParams) {
  const { value } = params;
  if (newGroup.value) {
    newGroup.value.icon = value as string;
  }
}

function changeAssignedEntities(val: ConfiguredEntity[]) {
  assignedEntities.value = val;
}

function startCreateGroup() {
  if (assignedEntities.value.length > 0) {
    createGroup();
  } else {
    dialogCreateGroup.value.open();
  }
}

async function createGroup() {
  if (assignedEntities.value.length > 0) {
    newGroup.value.entities = assignedEntities.value.map((entity) => {
      return entity.entity_id;
    })
  }

  try {
    const newItem = await profileStorage.createGroup(newGroup.value);
    if (addToPage.value == true) {
      addGroupToPage(newItem)
    }
    await sleep(2000);
    closeModal();
  } catch (e: any) {
    addErrorBottom(e);
  }
}

async function addGroupToPage(newItem: Group) {
  if (!props.page) {
    return;
  }

  let modifiedPage = JSON.parse(JSON.stringify(props.page))
  const itemsLength = modifiedPage.items.length;
  modifiedPage.items.push({
    group_id: newItem.group_id,
    pos: itemsLength + 1
  })
  try {
    await profileStorage.updatePage(modifiedPage as Page);
  } catch (e: any) {
    addErrorBottom(e, 'customise_remote.pages.update');
  }
}

function changePage(value: number) {
  pagination.value.page = value;
  fetchFilteredEntities();
}

function changePerPage(value: number) {
  pagination.value.page = 1;
  pagination.value.limit = value;
  fetchFilteredEntities();
}

function clearErrors() {
  if (errorMessage.value.length > 0) {
    errorMessage.value = '';
  }
}

</script>
<template>
  <Teleport to="body">
    <Modal
      :show="showModal"
      @close="closeModal"
      name="add-group"
      class="modal--steps modal--add modal--add-group">
      <template #header>
        <button
          v-show="activeStep == 2"
          @click="goToStep(1)"
          class="button button--tertiary button--icon modal__header__button-back"
          :class="{'button--secondary button--icon--small': isMobileScreen}">
          <i class="fa-light fa-arrow-left"></i>
        </button>
        <span class="modal__header__item--desktop">{{ $t("group.add.add_new_group") }}</span>
        <span class="modal__header__item--phone">{{ $t("group.add.new_group") }}</span>
      </template>
      <Transition :name="stepTransition">
        <div v-show="activeStep == 1" class="modal__body__step">
          <div class="modal__body__step__body modal--add-group__base-data" v-overflow-indicator>
            <IconSelect
              :key="newGroup.icon ? newGroup.icon : 'fa-regular fa-user'"
              :value="newGroup.icon ? newGroup.icon : 'fa-regular fa-user'"
              :change-callback="changeGroupIcon"
            />
            <UCInput
              v-model="newGroup.name"
              @click="clearErrors"
              :label="$t('form.name')"
              :error-message="errorMessage ? $t(errorMessage) : ''"
              :full-w="true"
              :focus="true"
            />
            <UCInput
              v-model="(newGroup.description as string)"
              @click="clearErrors"
              :type="'textarea'"
              :label="$t('form.description')"
              :full-w="true"
            />
            <UCToggle
              v-if="page"
              v-model="addToPage"
              :label="`${$t('group.add.add_to')} ${page && page.name ? page.name : ''}`"
              :full-w="true"
              :description="$t('group.add.add_to_description')"
            />
          </div>
        </div>
      </Transition>
      <Transition :name="stepTransition">
        <div v-show="activeStep == 2" class="modal__body__step modal__body__step--list">
          <div class="modal__body__step__body modal__body__step__body--list">
            <p class="modal__body__step__header-info">{{ $t("group.add.select_entities") }}</p>
            <EntityListFiltered
              @change-filter="changeFilter"
              @reload-entities="reloadEntities"
              @change-assigned-entities="changeAssignedEntities"
              @change-page="changePage"
              @change-per-page="changePerPage"
              :pagination="pagination"
              :all-entities="filteredEntities"
              :instances="instances"
              :has-action-buttons="false"
              :has-quick-options="true"
              :has-dropdown-menu="false"
              :parent="'add-group'"
            />
          </div>
        </div>
      </Transition>
      <template #footer>
        <button 
          v-if="activeStep == 1"
          @click="goToStep(2)"
          class="button button--primary button--min-w">
          {{ $t("ui.next") }}
        </button>
        <button
          v-else-if="activeStep == 2"
          @click="startCreateGroup"
          class="button button--primary button--min-w">
          {{ $t("ui.add") }}
        </button>
      </template>
    </Modal>
  </Teleport>
  <Dialog
    ref="dialogCreateGroup"
    @submit="createGroup"
    :title="$t('group.add.confirm_empty.title')"
    :text="$t('group.add.confirm_empty.question')"
    :submit-text="$t('ui.ok')"
    :cancel-text="$t('ui.cancel')"
  />
</template>